import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { catchError, map, mergeMap, skip, take } from 'rxjs/operators';
import { BehaviorSubject, forkJoin, iif, Observable, of, Subscription, throwError } from 'rxjs';
import { PayloadInterface } from '../../../models/interface/payload.interface';
import { RequestBodyData } from '../../../models/request-body.data';
import { HelperService } from '../../../services/helper/helper.service';
import { UserInterface } from '../../../models/interface/user.interface';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { ErrorInterface } from '../../../models/interface/error.interface';
import { StudyStore } from '../../../store/study/component-store/study.store';

type RoleSlug = 'study.ecoachmanager' | 'study.ecoach' | 'study.access' | 'study.publisher' | 'study.owner';

type Role = {
  id: number;
  role: RoleSlug;
};

export type StudyCollaboratorManagementDataParam = { intervention_name: string };

export type StudyCollaboratorManagementData = {
  param: StudyCollaboratorManagementDataParam;
  studyId: number;
  organisationId?: number;
  collaborators: UserInterface[];
  ecoaches: UserInterface[];
  myCollaborators?: UserInterface[];
  isAdmin: boolean;
};

@Component({
  selector: 'app-dialog-organisation-study-collaborator-management',
  templateUrl: './dialog-organisation-study-collaborator-management.component.html',
  styleUrls: ['./dialog-organisation-study-collaborator-management.component.scss'],
  providers: [StudyStore],
})
export class DialogOrganisationStudyCollaboratorManagementComponent implements OnInit, OnDestroy {
  public param: StudyCollaboratorManagementDataParam;
  public studyId: number;
  public organisationId: number;

  public collaborators: UserInterface[];
  public ecoaches: UserInterface[];
  public myNonCollaborators: UserInterface[] = [];

  // Filter
  public filter = { userSelection: '' };

  public ecoachesSubject = new BehaviorSubject<UserInterface[]>([]);
  public ecoaches$ = this.ecoachesSubject.asObservable();

  public collaboratorsSubject = new BehaviorSubject<UserInterface[]>([]);
  public collaborators$ = this.collaboratorsSubject.asObservable();

  // List of roles
  public roles = ['study.ecoachmanager', 'study.ecoach', 'study.access'] as const;
  public selectedRole: typeof this.roles[number] = 'study.access';

  public isOwner = false;
  public isOwnerCollaborator = false;

  public isReviewer = false;
  public isReviewerCollaborator = false;

  public selectedCollaborator: UserInterface | null = null;
  public selectedEcoach: UserInterface | null = null;
  public selectedMyCollaborator: UserInterface | null = null;

  isAdmin = false;

  // Form submission
  submitted = false;

  public selectedTabIndex = 0;

  public addCollaboratorResponse = new BehaviorSubject<string>('DEFAULT');
  public removeCollaboratorResponse = new BehaviorSubject<string>('DEFAULT');
  public updateCollaboratorResponse = new BehaviorSubject<string>('DEFAULT');

  public isNotRemovableSubject = new BehaviorSubject<boolean>(false);

  private addCollaboratorsResponse$: Observable<any>;
  private deleteCollaboratorsResponse$: Observable<any>;

  // Subscription Handler
  private subscriptions: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: StudyCollaboratorManagementData,
    private helperService: HelperService,
    private dialogRef: MatDialogRef<DialogOrganisationStudyCollaboratorManagementComponent>,
    private studyStore: StudyStore,
  ) {
    this.addCollaboratorsResponse$ = this.studyStore.addCollaboratorsResponse$;
    this.deleteCollaboratorsResponse$ = this.studyStore.deleteCollaboratorsResponse$;
  }

  // Helper function
  public get helper() {
    return this.helperService;
  }

  ngOnInit(): void {
    this.collaborators = this.data.collaborators;
    this.param = this.data.param;
    this.organisationId = this.data.organisationId;
    this.studyId = this.data.studyId;
    this.ecoaches = this.data.ecoaches;
    const myCollaborators = this.data.myCollaborators || null;
    this.isAdmin = this.data.isAdmin;

    if (this.selectedCollaborator === null && this.collaborators.length > 0) {
      this.selectedCollaborator = this.collaborators[0];
    }
    if (myCollaborators !== null) {
      this.myNonCollaborators = myCollaborators.filter(
        (orgCollaborator) => this.collaborators.every((collaborator) => collaborator.id != orgCollaborator.id),
      );
      if (this.myNonCollaborators.length > 0) {
        this.selectedMyCollaborator = this.myNonCollaborators[0];
      }
    }

    if (this.ecoaches.length > 0) {
      this.selectedEcoach = this.ecoaches[0];
    }
  }

  // Get all (multiple) roles of the user
  public getRolesOfCollaborator(user: UserInterface) {
    return user.relationships.roles.data;
  }

  public isNoneSelectedAddEcoachForm(): boolean {
    if (this.isAdmin) {
      return !this.selectedEcoach || !this.selectedRole;
    } else {
      return !this.selectedMyCollaborator || !this.selectedRole;
    }
  }

  public isNoneSelectedAddCollaboratorForm(): boolean {
    return !this.selectedCollaborator || !this.selectedRole;
  }

  // Adding collaborator to study
  public addCollaborator(): void {
    this.submitted = true;
    if (this.isNoneSelectedAddEcoachForm() || this.addCollaboratorResponse.value !== 'DEFAULT') {
      return;
    }
    this.addCollaboratorResponse.next('LOADING');
    const ecoach = this.isAdmin ? this.selectedEcoach : this.selectedMyCollaborator;
    if (!ecoach || !this.selectedRole) {
      this.addCollaboratorResponse.next('FAILURE');
      setTimeout(() => {
        this.submitted = false;
        this.addCollaboratorResponse.next('DEFAULT');
      }, 2500);
      return;
    }
    const addRoles: Role[] = [];
    addRoles.push({ id: ecoach.id, role: this.selectedRole });
    if (this.isReviewer) {
      addRoles.push({ id: ecoach.id, role: 'study.publisher' });
    }
    if (this.isOwner) {
      addRoles.push({ id: ecoach.id, role: 'study.owner' });
    }
    const payload: PayloadInterface = new RequestBodyData('users', { users: addRoles });
    this.studyStore.addCollaborators({ studyId: this.studyId, payload });
    this.subscriptions.push(
      this.addCollaboratorsResponse$
        .pipe(
          skip(1),
          take(1),
          mergeMap(
            (result) => iif(
              () => result instanceof HttpResponse,
              of(result),
              throwError(() => result),
            ),
          ),
        )
        .subscribe({
          next: () => {
            this.addCollaboratorResponse.next('SUCCESS');
          },
          error: () => {
            this.addCollaboratorResponse.next('FAILURE');
            setTimeout(() => {
              this.submitted = false;
              this.addCollaboratorResponse.next('DEFAULT');
            }, 2500);
          },
          complete: () => {
            setTimeout(() => {
              this.submitted = false;
              this.addCollaboratorResponse.next('DEFAULT');
              this.dialogRef.close('SUCCESS');
            }, 2500);
          },
        }),
    );
  }

  idEquals<T extends { id: number }>(user1: T, user2: T): boolean {
    return user1.id === user2.id;
  }

  public onChangeCollaborator(collaborator: UserInterface): void {
    // Form to add an existing editor to an study with a role
    const roles = this.getRolesOfCollaborator(collaborator);
    if (this.helper.hasRoles(roles, /study\.access$/)) {
      this.selectedRole = this.roles[2];
    } else if (this.helper.hasRoles(roles, /study\.ecoach$/)) {
      this.selectedRole = this.roles[1];
    } else if (this.helper.hasRoles(roles, /study\.ecoachmanager$/)) {
      this.selectedRole = this.roles[0];
    }
    this.isReviewerCollaborator = this.helper.hasRoles(roles, /study\.publisher$/);
    this.isOwnerCollaborator = this.helper.hasRoles(roles, /study\.owner$/);
  }

  // Adding collaborator to study
  public updateCollaborator(): void {
    this.submitted = true;
    if (this.updateCollaboratorResponse.value === 'DEFAULT') {
      this.updateCollaboratorResponse.next('LOADING');
      if (this.selectedCollaborator && this.selectedRole) {
        const removingRoles = this.roles.filter((role) => role !== this.selectedRole);
        const addRoles: Role[] = [];
        const removeRoles: Role[] = [];
        addRoles.push({ id: this.selectedCollaborator.id, role: this.selectedRole });
        if (this.isReviewerCollaborator) {
          addRoles.push({ id: this.selectedCollaborator.id, role: 'study.publisher' });
        } else {
          removeRoles.push({ id: this.selectedCollaborator.id, role: 'study.publisher' });
        }
        if (this.isOwnerCollaborator) {
          addRoles.push({ id: this.selectedCollaborator.id, role: 'study.owner' });
        } else {
          removeRoles.push({ id: this.selectedCollaborator.id, role: 'study.owner' });
        }
        removingRoles.forEach((role) => {
          removeRoles.push({ id: this.selectedCollaborator.id, role });
        });
        const payloadRemove: PayloadInterface = new RequestBodyData('users', { users: removeRoles });
        const payloadAdd: PayloadInterface = new RequestBodyData('users', { users: addRoles });

        const reqs: Observable<any>[] = [];
        this.studyStore.addCollaborators({ studyId: this.studyId, payload: payloadAdd });
        reqs.push(
          this.addCollaboratorsResponse$.pipe(
            skip(1),
            take(1),
            mergeMap(
              (result) => iif(
                () => result instanceof HttpResponse,
                of(result),
                throwError(() => result),
              ),
            ),
          ),
        );
        this.studyStore.deleteCollaborators({ studyId: this.studyId, payload: payloadRemove });
        reqs.push(
          this.deleteCollaboratorsResponse$.pipe(
            skip(1),
            take(1),
            mergeMap(
              (result) => iif(
                () => result instanceof HttpResponse,
                of(result),
                throwError(() => result),
              ),
            ),
          ),
        );
        this.subscriptions.push(
          forkJoin(reqs)
            .pipe(
              map((result) => result),
              catchError((error) => throwError(() => error)),
            )
            .subscribe({
              next: () => {
                this.updateCollaboratorResponse.next('SUCCESS');
                this.isNotRemovableSubject.next(false);
              },
              error: (error) => {
                this.updateCollaboratorResponse.next('FAILURE');
                if (typeof error.error !== 'string' && error.error && error instanceof HttpErrorResponse && error.error.errors) {
                  const errorData: ErrorInterface = error.error.errors[0];
                  if (errorData.status.toString() === '403' && errorData.detail.includes('is the only one guiding an intervention')) {
                    this.isNotRemovableSubject.next(true);
                  }
                }
                setTimeout(() => {
                  this.submitted = false;
                  this.updateCollaboratorResponse.next('DEFAULT');
                }, 2500);
              },
              complete: () => {
                setTimeout(() => {
                  this.submitted = false;
                  this.updateCollaboratorResponse.next('DEFAULT');
                  this.dialogRef.close('SUCCESS');
                }, 2500);
              },
            }),
        );
      }
    }
  }

  // Removing collaborator from study
  public removeCollaborator(): void {
    this.submitted = true;
    if (this.removeCollaboratorResponse.value === 'DEFAULT' && this.selectedCollaborator) {
      this.removeCollaboratorResponse.next('LOADING');
      const payload: PayloadInterface = new RequestBodyData('users', { users: [{ id: this.selectedCollaborator.id }] });

      this.studyStore.deleteCollaborators({ studyId: this.studyId, payload });
      this.subscriptions.push(
        this.deleteCollaboratorsResponse$
          .pipe(
            skip(1),
            take(1),
            mergeMap(
              (result) => iif(
                () => result instanceof HttpResponse,
                of(result),
                throwError(() => result),
              ),
            ),
          )
          .subscribe({
            next: () => {
              this.removeCollaboratorResponse.next('SUCCESS');
              this.isNotRemovableSubject.next(false);
            },
            error: (error) => {
              this.removeCollaboratorResponse.next('FAILURE');
              if (typeof error.error !== 'string' && error.error && error instanceof HttpErrorResponse && error.error.errors) {
                const errorData: ErrorInterface = error.error.errors[0];
                if (errorData.status.toString() === '403' && errorData.detail.includes('is the only one guiding an intervention')) {
                  this.isNotRemovableSubject.next(true);
                }
              }
              setTimeout(() => {
                this.submitted = false;
                this.removeCollaboratorResponse.next('DEFAULT');
              }, 2500);
            },
            complete: () => {
              setTimeout(() => {
                this.submitted = false;
                this.removeCollaboratorResponse.next('DEFAULT');
                this.dialogRef.close('SUCCESS');
              }, 2500);
            },
          }),
      );
    }
  }

  public setSelectedTabIndex(event: number): void {
    this.selectedTabIndex = event;
    this.isNotRemovableSubject.next(false);
  }

  public onKeyFilterECoaches(): void {
    const input = this.filter.userSelection.toLowerCase();
    const filterResults: UserInterface[] = this.ecoaches.filter(
      (user) =>
        this.helper.getCodeNameEmail(user.id, this.ecoaches).toLowerCase().includes(input) ||
          user.attributes.email.toLowerCase().includes(input),
    );
    this.ecoachesSubject.next(filterResults.length !== 0 ? filterResults : [this.selectedEcoach]);
  }

  public onKeyFilterCollaborators(): void {
    const input = this.filter.userSelection.toLowerCase();
    const filterResults: UserInterface[] = this.collaborators.filter(
      (user) =>
        user.attributes.name?.toLowerCase().includes(input) ||
          user.attributes.email.toLowerCase().includes(input),
    );
    this.collaboratorsSubject.next(filterResults.length !== 0 ? filterResults : [this.selectedCollaborator]);
  }

  public resetFilter(): void {
    this.filter.userSelection = '';
    this.ecoachesSubject.next(this.ecoaches);
    this.collaboratorsSubject.next(this.collaborators);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
